import { LTI_CSRF_COOKIE } from './cookies';

interface DeepLinkingSelection {
  quizSetId: string;
  entryMapHashId: string;
  quizTitle: string;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/**
 * Client-side LTI API client.
 * Used by the deep-linking page to submit quiz selection to the server.
 *
 * Pattern: apps/quiz-app/src/shared/moodle/MoodleClient.ts
 * (Unlike Moodle which uses postMessage, LTI uses server-side JWT signing)
 */
class LtiClientImpl {
  /**
   * Submit a quiz selection for Deep Linking.
   * The server signs a JWT and returns { jwt, returnUrl } for client-side form submission.
   */
  async submitDeepLinkingSelection(
    selection: DeepLinkingSelection,
  ): Promise<{ jwt: string; returnUrl: string }> {
    const response = await fetch('/api/lti/deep-linking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include JWE session cookie
      body: JSON.stringify(selection),
    });

    if (!response.ok) {
      throw new Error(`Deep linking submission failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Submit the signed Deep Linking JWT back to the LTI platform via auto-POST form.
   * Avoids innerHTML XSS risk by constructing the form programmatically.
   */
  submitDeepLinkingForm(returnUrl: string, jwt: string): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = returnUrl;
    form.style.display = 'none';
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'JWT';
    input.value = jwt;
    form.appendChild(input);
    document.body.appendChild(form);
    // Do NOT remove the form synchronously (or via setTimeout(0)): in Safari/iOS
    // navigation is kicked off asynchronously and a sibling timer can detach the
    // form before the request is actually dispatched, losing the submission.
    // Navigation tears down the document, so leaking this node is fine; if the
    // submit is blocked we prefer to keep the form in the DOM for retry/debug.
    form.submit();
  }

  /**
   * Submit quiz completion score for grade passback.
   */
  async submitGrade(data: {
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    timespent: number;
  }): Promise<{ success: boolean }> {
    // Double-submit CSRF: read the non-HttpOnly lti_csrf cookie and echo it
    // into the X-CSRF-Token header. The server compares this against the
    // csrf token embedded in the HttpOnly session cookie.
    const csrfToken = readCookie(LTI_CSRF_COOKIE);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

    const response = await fetch('/api/lti/grade', {
      method: 'POST',
      headers,
      credentials: 'include', // Include JWE session cookie
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorData: { error?: string; code?: string } = {};
      try {
        errorData = await response.json();
      } catch {
        // ignore parse error
      }
      const err = new Error(
        errorData.error || `Grade submission failed: ${response.status}`,
      );
      (err as Error & { code?: string }).code = errorData.code;
      throw err;
    }

    return response.json();
  }
}

export const LtiClient = new LtiClientImpl();
